/**
 * MemberEditDialog.tsx — 编辑团队成员（角色 / 状态 / 昵称，已接真，ADM 运营账号管理）
 *
 * 接真：adminUsersApi.update(id, {role?,status?,nickname?})（PATCH /api/v1/admin-users/:id），
 * 契约 UpdateAdminUserRequestSchema；后端 requireRole('root')，改角色/状态会 bump session_version。
 * 仅提交「有改动」的字段（避免无意义全量覆盖）；成功后 invalidate ['admin-users'] 刷新列表，失败错误 toast 不假成功。
 * 弹窗自身门控：非 root 会话不可保存（前端 UX，后端 root 专属为权威）。
 */

import type { AdminAccountResponse } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { adminUsersApi } from '~/hooks/use-admin-users';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UiFormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

const FormRow = (p: FormRowProps) => (
  <UiFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

const ROLE_OPTIONS: { value: 'root' | 'admin' | 'operator' | 'viewer'; label: string }[] = [
  { value: 'root', label: 'Root' },
  { value: 'admin', label: 'Admin' },
  { value: 'operator', label: '运营' },
  { value: 'viewer', label: '只读' },
];

export function MemberEditDialog({
  member,
  onClose,
}: { member: AdminAccountResponse; onClose: () => void }) {
  const isRoot = useAuthStore((s) => s.user?.role === 'root');
  const [role, setRole] = useState<AdminAccountResponse['role']>(member.role);
  const [accountCode, setAccountCode] = useState(member.account_code ?? '');
  const codeValid = !accountCode || /^[A-Z]{2,3}$/.test(accountCode);
  const [nickname, setNickname] = useState(member.nickname);
  // status: pending 成员只可看（尚未激活，不提供启停）；active⇄disabled 可切
  const [status, setStatus] = useState<'active' | 'disabled'>(
    member.status === 'disabled' ? 'disabled' : 'active',
  );
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: () => {
      // 仅提交有改动字段（PATCH 语义；nickname 至少 1 字符由服务端 Zod 兜底）
      const body: {
        role?: typeof role;
        status?: 'active' | 'disabled';
        nickname?: string;
        account_code?: string;
      } = {};
      if (accountCode && accountCode !== (member.account_code ?? ''))
        body.account_code = accountCode;
      if (role !== member.role) body.role = role;
      if (nickname.trim() && nickname.trim() !== member.nickname) body.nickname = nickname.trim();
      if (member.status !== 'pending' && status !== member.status) body.status = status;
      return adminUsersApi.update(member.id, body);
    },
    onSuccess: async (updated) => {
      // 同步 setQueryData 合并该行 + await invalidate 对账（checklist #2）
      queryClient.setQueryData<AdminAccountResponse[]>(['admin-users'], (prev) =>
        prev ? prev.map((m) => (m.id === updated.id ? updated : m)) : prev,
      );
      toast(`已更新成员 ${updated.nickname}`, { tone: 'pos', icon: 'IconCheck' });
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      onClose();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : '更新失败', { tone: 'neg', icon: 'IconAlert' });
    },
  });

  const dirty =
    (accountCode !== (member.account_code ?? '') && accountCode.length > 0) ||
    role !== member.role ||
    (nickname.trim() !== member.nickname && nickname.trim().length > 0) ||
    (member.status !== 'pending' && status !== member.status);

  return (
    <Dialog
      width={480}
      title={`编辑成员 · ${member.nickname}`}
      subtitle={member.email}
      icon="IconShield"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            onClick={() => updateMutation.mutate()}
            disabled={
              !isRoot ||
              !dirty ||
              !codeValid ||
              (!!member.account_code && !accountCode) ||
              updateMutation.isPending
            }
            title={isRoot ? undefined : '仅 root 可管理运营账号（后端 requireRole）'}
          >
            保存
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="昵称" required>
          <input
            className="input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="成员昵称"
            style={{ maxWidth: 280 }}
            disabled={!isRoot}
          />
        </FormRow>
        <FormRow label="账号 CODE" required>
          <div>
            <input
              className="input"
              aria-label="账号 CODE"
              value={accountCode}
              maxLength={3}
              onChange={(e) => setAccountCode(e.target.value.toUpperCase())}
              placeholder="例如 YG / CXN"
              disabled={!isRoot}
              style={{ maxWidth: 200 }}
            />
            <div className="text-muted" style={{ marginTop: 6, fontSize: 11 }}>
              2–3 位英文字母，全局唯一；用于新任务的 PR 署名。
            </div>
            {!codeValid && <div role="alert">请输入 2–3 位英文字母</div>}
          </div>
        </FormRow>
        <FormRow label="角色" required>
          <Select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as typeof role)}
            style={{ maxWidth: 200 }}
            disabled={!isRoot}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="状态">
          {member.status === 'pending' ? (
            <span className="chip chip-warn" style={{ fontSize: 10.5 }}>
              邀请中（接受邀请后激活）
            </span>
          ) : (
            <Select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'active' | 'disabled')}
              style={{ maxWidth: 200 }}
              disabled={!isRoot}
            >
              <option value="active">已启用</option>
              <option value="disabled">已停用</option>
            </Select>
          )}
        </FormRow>
      </div>
    </Dialog>
  );
}
